import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { useCategories } from '../hooks/useCategories';
import { GraduationCap, BookOpen, ChevronRight, Sparkles, School, Search } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Onboarding modal shown after first-time student registration.
 * Asks: School Type → Specific Level → School (optional)
 */
export default function StudentOnboarding({ onComplete }) {
  const { user, login } = useContext(AuthContext);
  const { levelGroups } = useCategories();
  const [step, setStep] = useState(1);
  const [schoolType, setSchoolType] = useState('');
  const [studentLevel, setStudentLevel] = useState('');
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [schools, setSchools] = useState([]);
  const [schoolSearch, setSchoolSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const shouldShow = user && user.role === 'student' && !user.onboarding_complete && !user.student_level;

  useEffect(() => {
    if (!shouldShow && onComplete) onComplete();
  }, [shouldShow, onComplete]);

  useEffect(() => {
    if (step === 3) {
      axios.get(`${API}/schools`).then(r => setSchools(r.data || [])).catch(() => {});
    }
  }, [step]);

  if (!shouldShow) return null;

  const schoolTypes = [
    { id: 'primary', label: 'Primary School', desc: 'Grade 1 to Grade 7', icon: '1-7', color: 'emerald' },
    { id: 'secondary', label: 'Secondary School', desc: 'O-Level & A-Level', icon: 'I-VI', color: 'blue' },
  ];

  const getLevels = () => {
    if (!schoolType) return [];
    if (schoolType === 'primary') {
      const primaryGroup = levelGroups.find(g => g.name.toLowerCase() === 'primary');
      return primaryGroup ? primaryGroup.levels.map(l => l.name) : ['Grade 1','Grade 2','Grade 3','Grade 4','Grade 5','Grade 6','Grade 7'];
    }
    const oLevel = levelGroups.find(g => g.name.toLowerCase() === 'o-level');
    const aLevel = levelGroups.find(g => g.name.toLowerCase() === 'a-level');
    const levels = [];
    if (oLevel) levels.push(...oLevel.levels.map(l => l.name));
    if (aLevel) levels.push(...aLevel.levels.map(l => l.name));
    return levels.length ? levels : ['Form I','Form II','Form III','Form IV','Form V','Form VI'];
  };

  const handleSave = async () => {
    if (!schoolType || !studentLevel) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const body = { school_type: schoolType, student_level: studentLevel, onboarding_complete: true };
      if (selectedSchool) body.school_id = selectedSchool.id;

      const res = await fetch(`${API}/auth/me`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const updated = await res.json();
        login(token, updated);

        // Enroll to school if selected
        if (selectedSchool) {
          try {
            await axios.post(`${API}/schools/${selectedSchool.id}/enroll`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch {}
        }

        if (onComplete) onComplete();
      }
    } catch (err) {
      console.error('Onboarding save failed:', err);
    }
    setSaving(false);
  };

  const filteredSchools = schools.filter(s =>
    s.name?.toLowerCase().includes(schoolSearch.toLowerCase()) ||
    s.region?.toLowerCase().includes(schoolSearch.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70] p-4" data-testid="student-onboarding-modal">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Help us serve you better</h2>
              <p className="text-indigo-200 text-sm">Choose your level so we can recommend the best content for you</p>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <div className={`h-1 flex-1 rounded-full ${step >= 1 ? 'bg-white' : 'bg-white/30'}`} />
            <div className={`h-1 flex-1 rounded-full ${step >= 2 ? 'bg-white' : 'bg-white/30'}`} />
            <div className={`h-1 flex-1 rounded-full ${step >= 3 ? 'bg-white' : 'bg-white/30'}`} />
          </div>
        </div>

        {/* Step 1: School Type */}
        {step === 1 && (
          <div className="p-6 space-y-4">
            <h3 className="text-white font-semibold">What school level are you?</h3>
            <div className="grid grid-cols-2 gap-3">
              {schoolTypes.map(type => (
                <button key={type.id} onClick={() => { setSchoolType(type.id); setStudentLevel(''); setStep(2); }}
                  data-testid={`school-type-${type.id}`}
                  className={`p-5 rounded-xl border-2 text-left transition-all hover:scale-[1.02] ${schoolType === type.id ? `border-${type.color}-500 bg-${type.color}-950/30` : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'}`}>
                  <div className={`w-12 h-12 rounded-xl bg-${type.color}-600/20 flex items-center justify-center mb-3`}>
                    {type.id === 'primary' ? <BookOpen className={`w-6 h-6 text-${type.color}-400`} /> : <GraduationCap className={`w-6 h-6 text-${type.color}-400`} />}
                  </div>
                  <p className="text-white font-semibold text-sm">{type.label}</p>
                  <p className="text-slate-400 text-xs mt-0.5">{type.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Specific Level */}
        {step === 2 && (
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <button onClick={() => setStep(1)} className="text-slate-400 hover:text-white text-sm">
                {schoolType === 'primary' ? 'Primary School' : 'Secondary School'}
              </button>
              <ChevronRight className="w-3 h-3 text-slate-500" />
              <span className="text-white text-sm font-medium">Choose your level</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {getLevels().map(level => (
                <button key={level} onClick={() => setStudentLevel(level)}
                  data-testid={`level-option-${level.replace(/\s/g, '-')}`}
                  className={`px-4 py-3 rounded-xl border text-sm font-medium transition-all ${studentLevel === level ? 'border-indigo-500 bg-indigo-600/20 text-indigo-300' : 'border-slate-700 bg-slate-800/50 text-slate-300 hover:border-slate-600 hover:text-white'}`}>
                  {level}
                </button>
              ))}
            </div>
            <Button onClick={() => setStep(3)} disabled={!studentLevel}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 mt-2" data-testid="level-continue-btn">
              Continue
            </Button>
          </div>
        )}

        {/* Step 3: School Selection (optional) */}
        {step === 3 && (
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <button onClick={() => setStep(2)} className="text-slate-400 hover:text-white text-sm">Level</button>
              <ChevronRight className="w-3 h-3 text-slate-500" />
              <span className="text-white text-sm font-medium">Select your school</span>
            </div>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-700 bg-slate-800 text-white text-sm placeholder-slate-500"
                placeholder="Search schools..." value={schoolSearch} onChange={e => setSchoolSearch(e.target.value)} />
            </div>
            <div className="max-h-48 overflow-y-auto space-y-2 scrollbar-thin">
              {filteredSchools.map(school => (
                <button key={school.id} onClick={() => setSelectedSchool(school)}
                  data-testid={`school-option-${school.id}`}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${selectedSchool?.id === school.id ? 'border-indigo-500 bg-indigo-600/20' : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'}`}>
                  {school.thumbnail ? <img src={school.thumbnail} alt="" className="w-10 h-10 rounded-lg object-cover" /> :
                    <div className="w-10 h-10 rounded-lg bg-indigo-600/20 flex items-center justify-center"><School className="w-5 h-5 text-indigo-400" /></div>
                  }
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{school.name}</p>
                    {school.region && <p className="text-slate-400 text-xs">{school.region}{school.district ? `, ${school.district}` : ''}</p>}
                  </div>
                </button>
              ))}
              {filteredSchools.length === 0 && <p className="text-center text-slate-500 text-sm py-4">No schools found</p>}
            </div>
            <div className="flex gap-3">
              <Button onClick={handleSave} disabled={saving}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40" data-testid="onboarding-save-btn">
                {saving ? 'Saving...' : selectedSchool ? `Join ${selectedSchool.name}` : 'Skip & Continue'}
              </Button>
            </div>
            {selectedSchool && (
              <button onClick={() => setSelectedSchool(null)} className="text-slate-400 hover:text-white text-xs text-center w-full">
                Clear school selection
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
